import type { ReactNode } from 'react';

import {
  CtdDossierContext,
  Field,
  GatingSubsection,
  NumberedSection,
  ProseSubsection,
  SubsectionHeading,
  obj,
} from './DossierPrimitives';
import {
  Module2,
  Module3,
  Module4,
  Module5,
  RegaraNotesSection,
  letterWithoutCitations,
} from './EctdRenderer';
import { EctdBibliography, EctdDisclaimer } from './EctdFrontMatter';
import type { EctdFrontMatter } from '@/features/dossier/api';
import { CtdIdentityCard } from './CtdIdentityCard';

/**
 * EU drug & biologic (CTD) results page — the EU Module 1 + the ICH-harmonised
 * Modules 2–5 shared with the US view. Mirrors the EU CTD PDF
 * (`backend/services/dossier/ectd_renderer/eu.py`): same Module 1 sections, labels
 * and hints. Before this view the page showed EU dossiers in the US layout — FDA
 * Module 1 fields and 21 CFR citations (memo M-RA-261009-BA 001).
 */

const AUTHORITY = 'EU';

// Same note as the PDF (`_REFORM_NOTE` in ectd_renderer/eu.py).
const REFORM_NOTE =
  'Legal framework: Directive 2001/83/EC + Regulation (EC) 726/2004 (valid through the ' +
  '2026-2028 transition to the new EU pharmaceutical legislation). Confirm the currently-' +
  'applicable provisions for this application.';

type Prose = { body?: unknown; citations?: unknown[] } | null;

/**
 * The REGARA check notes on the application form, key and caption, in print order: they show in
 * the "REGARA Notes" section before Module 1 (founder 2026-11-02, every country alike), as the
 * Word file prints them. LOCKSTEP with the "note" rows of `EAF_ROWS`
 * (`app/dossier/eu_medicinal/rules/ctd_module_1.py`); tests/test_eu_medicinal_module1_lockstep.py.
 */
const EU_REGARA_NOTES = [
  // eu_medicinal.checks.applicant._set_applicant_note
  { key: 'applicant_note', caption: 'Applicant check (review before submission)' },
  // eu_medicinal.checks.sites.apply_site_check
  { key: 'site_note', caption: 'Manufacturers check (review before submission)' },
  // eu_medicinal.checks.standards.apply_standards_check
  { key: 'standards_note', caption: 'Standards check (review before submission)' },
  // eu_medicinal.checks.gating.apply_section_gates
  { key: 'legal_basis_note', caption: 'Legal basis check (review before submission)' },
  // eu_medicinal.checks.procedure.apply_centralised_scope_note
  { key: 'procedure_note', caption: 'Procedure check (review before submission)' },
  // eu_medicinal.checks.hta_jca.apply_hta_jca_note: a parallel submission, never part of the CTD.
  { key: 'hta_jca_note', caption: 'EU HTA JCA scope (review before submission)' },
  // eu_medicinal.checks.hta_jca: the orphan designation needs a second look.
  { key: 'orphan_status_note', caption: 'Orphan status (review before submission)' },
] as const;

export function EuCtdRenderer({
  dossier,
  frontMatter = null,
  headingRow,
}: {
  dossier: Record<string, unknown>;
  /** The cover rows, bibliography and disclaimer the server builds for the Word file too
   *  (`ectd_front_matter` on the run response); null shows none of them. */
  frontMatter?: EctdFrontMatter | null;
  /** The dossier title and the download button, as one row. Rendered BELOW the identity block
   *  and outside Module 1's card — where the device page puts its "Part 1 — …" row. */
  headingRow?: ReactNode;
}) {
  const m1 = obj(dossier, 'module_1_administrative');
  const af = obj(m1, 'application_form');
  // As in the eCTD PDF: letters keep their line breaks, AI tables are tables, no device wording.
  return (
    <CtdDossierContext.Provider value>
      <div className="space-y-4">
        {/* The cover's identity block - what a reviewer establishes first. */}
        {frontMatter && <CtdIdentityCard rows={frontMatter.cover_rows} />}
        {headingRow}
        <RegaraNotesSection
          notes={EU_REGARA_NOTES.map(({ key, caption }) => ({ key, caption, body: af[key] }))}
        />
        <EuModule1 m1={m1} legalBasisLabel={frontMatter?.legal_basis_label ?? null} />
        <Module2 m2={obj(dossier, 'module_2_summaries')} />
        <Module3
          m3={obj(dossier, 'module_3_quality')}
          regionalHint="EU-specific quality information"
        />
        <Module4 m4={obj(dossier, 'module_4_nonclinical')} authority={AUTHORITY} />
        <Module5 m5={obj(dossier, 'module_5_clinical')} authority={AUTHORITY} />
        {frontMatter && <EctdBibliography frontMatter={frontMatter} />}
        {frontMatter && <EctdDisclaimer frontMatter={frontMatter} />}
      </div>
    </CtdDossierContext.Provider>
  );
}

// ── Module 1 — EU Regional Administrative Information ───────────────────

function EuModule1({
  m1,
  legalBasisLabel,
}: {
  m1: Record<string, unknown>;
  /** The pathway's label from the server (`ectd_front_matter.legal_basis_label`), as the Word
   *  file prints it when the run wrote no declaration. */
  legalBasisLabel: string | null;
}) {
  const af = obj(m1, 'application_form');
  const pi = obj(m1, 'product_information');
  // LOCKSTEP with procedure.product_number_hint (the server's cover rows and the Word file read
  // it): the EMA product number is a centralised-procedure identifier and no other has one.
  const isCentralised =
    typeof af.procedure === 'string' && af.procedure.startsWith('Centralised procedure');
  const designations = Array.isArray(af.designations)
    ? af.designations.filter((d): d is string => typeof d === 'string' && d.trim() !== '')
    : [];
  // The agent writes the declaration as a string or as {body} (the PDF accepts both); the
  // pathway's label stands in when it is missing.
  const raw = m1.legal_basis_declaration;
  const written = typeof raw === 'string' ? raw : obj(m1, 'legal_basis_declaration').body;
  const declaration =
    typeof written === 'string' && written.trim() ? written : legalBasisLabel;
  return (
    <NumberedSection prefix="Module" num={1} title="EU Regional Administrative Information">
      <ProseSubsection
        number="1.0"
        title="Cover Letter"
        prose={letterWithoutCitations(m1.cover_letter)}
        placeholderHint="Cover letter to the EMA / national competent authority"
      />
      <ProseSubsection
        number="1.5.x"
        title="Legal basis of the application"
        prose={{ body: declaration }}
        placeholderHint="Article of Directive 2001/83/EC relied upon + reference product (if any)"
      />

      <SubsectionHeading number="1.2" title="Application Form (eAF)" />
      <Field
        label="(Invented) name of the medicinal product"
        value={af.product_name}
        fallbackHint="Proposed (invented) name"
      />
      <Field
        label="Active substance(s) / INN"
        value={af.active_substance}
        fallbackHint="Active substance(s) / INN"
      />
      <Field
        label="Pharmaceutical form and strength"
        value={af.pharmaceutical_form_strength}
        fallbackHint="Pharmaceutical form and strength"
      />
      <Field label="ATC code" value={af.atc_code} fallbackHint="ATC code" />
      <Field
        label="Applicant (marketing authorisation holder)"
        value={af.applicant}
        fallbackHint="Applicant / MAH name and address"
      />
      <Field
        label="Qualified Person for Pharmacovigilance (QPPV)"
        value={af.qppv}
        fallbackHint="QPPV name + contact (Article 104, Directive 2001/83/EC)"
      />
      <Field
        label="Procedure"
        value={af.procedure}
        fallbackHint="Centralised / Decentralised / Mutual Recognition / National"
      />
      {/* The product number persists for the life of the MA; the procedure number names
          this application (RA, 2026-10-26). CENTRALISED ONLY, in lockstep with the PDF's
          _eaf_row and with cover_identity_rows: a decentralised or national application
          never gets an EMEA/H/C number, and an unset Field renders a hint asking the
          submitter to supply one — the 2026-10-13 staging bug. */}
      {isCentralised && (
        <Field
          label="EMA product number"
          value={af.product_number}
          fallbackHint="EMA product number (EMEA/H/C/xxxxxx)"
        />
      )}
      {/* Designations verified against the run's inputs (eu_medicinal.checks.designations); absent
          when none — lockstep with the PDF's _designations_field. */}
      {designations.length > 0 && (
        <Field label="Designations and opinions" value={designations.join('; ')} />
      )}

      <ProseSubsection
        number="1.3"
        title="Product Information (SmPC, labelling, package leaflet)"
        prose={pi.smpc_labelling_pil as Prose}
        placeholderHint="Verbatim SmPC, labelling and package leaflet (Annex I/II/III)"
      />
      <ProseSubsection
        number="1.4"
        title="Information about the Experts"
        prose={m1.expert_declarations as Prose}
        placeholderHint="Quality / nonclinical / clinical expert declarations and CVs"
      />
      <ProseSubsection
        number="1.6"
        title="Environmental Risk Assessment (ERA)"
        prose={null}
        placeholderHint="ERA per EMEA/CHMP/SWP/4447/00 Rev. 1 — Phase I screening and, if triggered, Phase II for a chemical substance; for proteins and other natural substances, a justification for not submitting ERA studies"
      />
      <ProseSubsection
        number="1.8"
        title="Pharmacovigilance (RMP / PSMF)"
        prose={m1.pharmacovigilance as Prose}
        placeholderHint="Risk Management Plan + Pharmacovigilance System Master File reference"
      />
      <GatingSubsection
        number="1.10"
        title="Paediatrics (PIP)"
        gating={m1.paediatric as { gating_response?: unknown; rationale?: unknown } | undefined}
        question="Is a Paediatric Investigation Plan (PIP) / waiver applicable to this application?"
        authority={AUTHORITY}
      />
      <p className="mt-4 mb-0 text-[12.5px] italic text-muted">{REFORM_NOTE}</p>
    </NumberedSection>
  );
}
